// @ts-nocheck
/**
 * Assignments and Assessments Service
 * Manages standards-based assignments, student enrollments, and student-by-standard grading grids.
 */

import { API_URL, authFetch } from './api';
import { submissionStatusFor, timelinessComponents, timelinessIsReady, timelinessStandard } from '../utils/taskTimeliness';

const STORAGE_KEY = 'academic_eye_teacher_assignments_v1';

export const SCORING_METHODS = {
  MASTERY: 'mastery', // Academic Eye 4-Level: 1 Not Yet, 2 Learning Zone, 3 On Track, 4 Proficient
  POINTS: 'points',   // Numeric score (e.g. 0-100 or custom max)
  RUBRIC: 'rubric',   // 4-level rubric rating
};

export const ASSIGNMENT_TYPES = [
  { value: 'Assignment', label: 'Assignment' },
  { value: 'Quiz', label: 'Quiz' },
  { value: 'Test', label: 'Test' },
  { value: 'Project', label: 'Project' },
  { value: 'Observation', label: 'Observation' },
  { value: 'Custom', label: 'Custom' },
];

export const ASSIGNMENT_STATUSES = [
  { value: 'draft', label: 'Draft', color: '#6b7280' },
  { value: 'assigned', label: 'Assigned', color: '#2563eb' },
  { value: 'in_progress', label: 'In Progress', color: '#d97706' },
  { value: 'graded', label: 'Graded', color: '#059669' },
  { value: 'completed', label: 'Completed', color: '#10b981' },
];

// No sample or mock assignments: only real user assignments from backend / storage
export const DEFAULT_SAMPLE_ASSIGNMENTS = [];

export function generateAssignmentsFromTeacherClasses() {
  return [];
}

function isSampleAssignment(a) {
  if (!a || !a.id) return true;
  const id = String(a.id);
  if (
    id === 'asgn-lit-601' ||
    id === 'asgn-math-602' ||
    id === 'asgn-sci-603' ||
    id.startsWith('asgn-live-') ||
    id.startsWith('asgn-sample-')
  ) {
    return true;
  }
  if (typeof a.title === 'string' && a.title.includes('Unit 1 Standards Mastery') && id.startsWith('asgn-')) {
    return true;
  }
  return false;
}

function readLocalAssignments() {
  const items = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k !== STORAGE_KEY && !(k && k.startsWith(`${STORAGE_KEY}_`))) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(k));
        if (Array.isArray(parsed)) items.push(...parsed.map((a) => ({ a, k })));
      } catch (_: any) {}
    }
  } catch (_: any) {}
  return items.filter(({ a }) => !isSampleAssignment(a));
}

async function apiJson(path, options = {}) {
  const response = await authFetch(`${API_URL}/api/learning-tasks${path}`, {
    ...options,
    headers: options.body && !(options.body instanceof FormData)
      ? { 'Content-Type': 'application/json', ...(options.headers || {}) }
      : options.headers,
  });
  let data = null;
  try { data = await response.json(); } catch (_: any) {}
  if (!response.ok) {
    const detail = data && typeof data.detail === 'string' ? data.detail : null;
    throw new Error(detail || `Learning task request failed (${response.status}).`);
  }
  return data;
}

let importPromise = null;

/**
 * One-time move of learning tasks saved in this browser (older versions) onto the server, so they
 * survive cache clears and reach students and families. Local copies are removed once the server has them.
 */
export async function importBrowserAssignments() {
  const local = readLocalAssignments();
  if (!local.length) return { imported: [], skipped: [] };
  if (!importPromise) {
    importPromise = (async () => {
      const result = await apiJson('/import', { method: 'POST', body: JSON.stringify({ tasks: local.map(({ a }) => a) }) });
      const server = await apiJson('');
      const onServer = new Set([...(result.imported || []), ...(server || []).map((t) => t.id)]);
      const keys = new Set(local.map(({ k }) => k));
      keys.forEach((k) => {
        try {
          const remaining = (JSON.parse(localStorage.getItem(k)) || []).filter((a) => !onServer.has(a?.id) && !isSampleAssignment(a));
          if (remaining.length) localStorage.setItem(k, JSON.stringify(remaining));
          else localStorage.removeItem(k);
        } catch (_: any) {
          localStorage.removeItem(k);
        }
      });
      return result;
    })().finally(() => { importPromise = null; });
  }
  return importPromise;
}

/**
 * Calculate completion and mastery statistics for an assignment
 */
export function calculateGradingStats(assignment) {
  if (!assignment) return { totalCells: 0, gradedCells: 0, percentComplete: 0, distribution: {} };

  const students = assignment.students || [];
  const standards = assignment.standards || [];
  const totalCells = students.length * standards.length;

  let gradedCells = 0;
  let missingCells = 0;
  let naCells = 0;
  const distribution = {
    proficient: 0,  // 4 / green
    onTrack: 0,     // 3 / lightgreen
    learningZone: 0,// 2 / yellow
    notYet: 0,      // 1 / red
  };

  const standardStats = {};
  standards.forEach((std) => {
    standardStats[std.mlsCode] = {
      mlsCode: std.mlsCode,
      friendlyTitle: std.friendlyTitle || std.mlsCode,
      gradedCount: 0,
      totalCount: students.length,
      sumScore: 0,
      average: null,
      proficient: 0,
      onTrack: 0,
      learningZone: 0,
      notYet: 0,
    };
  });

  students.forEach((stu) => {
    const stuGrades = assignment.grades?.[stu.studentId] || {};
    standards.forEach((std) => {
      const entry = stuGrades[std.mlsCode];
      if (!entry) return;

      if (entry.isMissing || entry.score === 'M') {
        missingCells += 1;
        gradedCells += 1;
        return;
      }

      if (entry.notAssessed || entry.score === 'NA') {
        naCells += 1;
        gradedCells += 1;
        return;
      }

      const num = Number(entry.score);
      if (Number.isFinite(num) && entry.score !== '') {
        gradedCells += 1;
        const sStat = standardStats[std.mlsCode];
        if (sStat) {
          sStat.gradedCount += 1;
          sStat.sumScore += num;
        }

        if (num >= 4 || entry.color === 'green') {
          distribution.proficient += 1;
          if (sStat) sStat.proficient += 1;
        } else if (num >= 3 || entry.color === 'lightgreen') {
          distribution.onTrack += 1;
          if (sStat) sStat.onTrack += 1;
        } else if (num >= 2 || entry.color === 'yellow') {
          distribution.learningZone += 1;
          if (sStat) sStat.learningZone += 1;
        } else {
          distribution.notYet += 1;
          if (sStat) sStat.notYet += 1;
        }
      }
    });
  });

  // Calculate averages per standard
  Object.values(standardStats).forEach((s) => {
    if (s.gradedCount > 0) {
      s.average = (s.sumScore / s.gradedCount).toFixed(1);
    }
  });

  const percentComplete = totalCells > 0 ? Math.round((gradedCells / totalCells) * 100) : 0;

  return {
    totalCells,
    gradedCells,
    missingCells,
    naCells,
    percentComplete,
    distribution,
    standardStats,
  };
}

/**
 * Fetch all assignments with optional query filters
 */
async function importBrowserAssignmentsQuietly() {
  try {
    await importBrowserAssignments();
  } catch (e: any) {
    console.warn('[AssignmentsService] Could not move browser-saved learning tasks to the server:', e);
  }
}

export async function fetchAssignments({ status, subject, classId, search } = {}) {
  await importBrowserAssignmentsQuietly();
  let filtered = (await apiJson('')) || [];

  if (status && status !== 'all') {
    filtered = filtered.filter((a) => a.status === status);
  }
  if (subject && subject !== 'all') {
    filtered = filtered.filter((a) => (a.subject || '').toLowerCase() === subject.toLowerCase());
  }
  if (classId && classId !== 'all') {
    filtered = filtered.filter((a) => String(a.classId) === String(classId));
  }
  if (search && search.trim()) {
    const q = search.toLowerCase().trim();
    filtered = filtered.filter((a) =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.className || '').toLowerCase().includes(q) ||
      (a.subject || '').toLowerCase().includes(q) ||
      (a.standards || []).some((s) =>
        (s.mlsCode || '').toLowerCase().includes(q) ||
        (s.friendlyTitle || '').toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
      )
    );
  }

  // Sort descending by updated/created date
  filtered.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
  return filtered;
}

/**
 * Fetch a single assignment by ID
 */
export async function fetchAssignmentById(id) {
  await importBrowserAssignmentsQuietly();
  return apiJson(`/${encodeURIComponent(id)}`);
}

/**
 * Create a new assignment
 */
export async function createAssignment(assignmentData) {
  const now = new Date().toISOString();
  return apiJson('', {
    method: 'POST',
    body: JSON.stringify({ task: {
      ...assignmentData,
      id: assignmentData.id || `asgn-${Date.now()}`,
      status: assignmentData.status || 'assigned',
      grades: assignmentData.grades || {},
      studentNotes: assignmentData.studentNotes || {},
      createdAt: now,
      updatedAt: now,
    } }),
  });
}

/**
 * Update assignment details
 */
export async function updateAssignment(id, patch) {
  const { canEdit, createdBy, ...task } = patch || {};
  return apiJson(`/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ task }) });
}

/**
 * Save/update grades and notes in the student-by-standard grading grid
 */
export async function updateAssignmentGrades(id, grades, studentNotes, newStatus, teacherId = null, submissionStatuses = null) {
  // Merge onto the latest server copy so co-teachers grading at the same time don't overwrite each other.
  const existing = await fetchAssignmentById(id);
  const updatedGrades = { ...(existing.grades || {}), ...grades };
  const updatedNotes = { ...(existing.studentNotes || {}), ...studentNotes };

  // Auto-advance status if not explicitly specified
  let status = newStatus || existing.status;
  if (!newStatus) {
    const stats = calculateGradingStats({ ...existing, grades: updatedGrades });
    // Grades are only released to students/families by "Finalize & Publish Grades".
    if (stats.percentComplete > 0 && status === 'assigned') {
      status = 'in_progress';
    }
  }

  return updateAssignment(id, {
    grades: updatedGrades,
    studentNotes: updatedNotes,
    submissionStatuses: submissionStatuses || existing.submissionStatuses || {},
    status,
  });
}

/**
 * Duplicate an existing assignment
 */
export async function duplicateAssignment(id) {
  const source = await fetchAssignmentById(id);
  const { canEdit, createdBy, ...rest } = source;
  return createAssignment({
    ...rest,
    id: `asgn-${Date.now()}`,
    title: `${source.title} (Copy)`,
    status: 'draft',
    grades: {},
    studentNotes: {},
    submissionStatuses: {},
    syncedDataPoints: {},
  });
}

/**
 * Delete an assignment
 */
export async function deleteAssignment(id) {
  return apiJson(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Upload a file for a learning task or a reply. Returns the attachment record to save.
 */
export async function uploadTaskFile(file) {
  const form = new FormData();
  form.append('file', file);
  return apiJson('/files', { method: 'POST', body: form });
}

/**
 * Open an uploaded learning task file in a new tab.
 */
export async function openTaskFile(attachment, taskId) {
  const params = new URLSearchParams({ key: attachment.key });
  if (taskId) params.set('task_id', taskId);
  const popup = window.open('', '_blank');
  try {
    const response = await authFetch(`${API_URL}/api/learning-tasks/files?${params}`);
    if (!response.ok) throw new Error(response.status === 403 ? 'You do not have access to this file.' : 'File could not be opened.');
    const blob = await response.blob();
    const typed = attachment.mimeType && blob.type !== attachment.mimeType ? new Blob([blob], { type: attachment.mimeType }) : blob;
    const url = URL.createObjectURL(typed);
    if (popup) popup.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e: any) {
    if (popup) popup.close();
    throw e;
  }
}

/** Published tasks for the signed-in student, or for each linked child of a family. */
export async function fetchMyTasks() {
  return (await apiJson('/mine')) || [];
}

/** Per-student reply thread summaries for a task (teachers and admins). */
export async function fetchTaskThreads(taskId) {
  return (await apiJson(`/${encodeURIComponent(taskId)}/threads`)) || [];
}

const threadPath = (taskId, studentId, channel) =>
  `/${encodeURIComponent(taskId)}/threads/${encodeURIComponent(studentId)}?channel=${encodeURIComponent(channel)}`;

// channel: 'student' = submission conversation (student + teachers);
// 'family' = family + teachers, opens once grades are finalized.
export async function fetchTaskThread(taskId, studentId, channel = 'student') {
  return (await apiJson(threadPath(taskId, studentId, channel))) || [];
}

export async function postTaskReply(taskId, studentId, { body = '', attachments = [] } = {}, channel = 'student') {
  return apiJson(threadPath(taskId, studentId, channel), {
    method: 'POST',
    body: JSON.stringify({ body, attachments }),
  });
}

/**
 * Publish/sync evaluated assignment grades directly to Academic Eye's data points
 */
export async function publishAssignmentGradesToDataPoints(assignment, grades, teacherId = null) {
  if (!assignment) return { count: 0, total: 0 };

  const standards = assignment.standards || [];
  const students = assignment.students || [];
  const payloads = [];
  const components = timelinessComponents(assignment);
  const timingStandard = timelinessStandard(assignment.timeliness?.grade || assignment.grade);
  if (assignment.timeliness?.enabled && (!timingStandard || !components.length)) {
    throw new Error('Timeliness grading needs a supported task grade band and at least one component. Edit the learning task.');
  }

  students.forEach((stu) => {
    // Only real numeric student IDs can be persisted to the backend
    const stuNum = Number(stu.studentId);
    if (!Number.isFinite(stuNum)) return;

    const stuGrades = grades?.[stu.studentId] || {};
    standards.forEach((std) => {
      const cell = stuGrades[std.mlsCode];
      if (!cell || cell.score === '' || cell.score === null || cell.isMissing || cell.notAssessed) return;

      const scoreNum = Number(cell.score);
      if (!Number.isFinite(scoreNum)) return;

      let finalScore = Math.round(scoreNum);
      let finalColor = cell.color || null;

      // Handle 4-level mastery scale to standard percentage scores & colors
      if (scoreNum >= 1 && scoreNum <= 4 && assignment.scoringMethod !== 'points') {
        if (scoreNum === 4) { finalScore = 95; finalColor = 'green'; }
        else if (scoreNum === 3) { finalScore = 85; finalColor = 'lightgreen'; }
        else if (scoreNum === 2) { finalScore = 70; finalColor = 'yellow'; }
        else if (scoreNum === 1) { finalScore = 45; finalColor = 'red'; }
      } else if (!finalColor) {
        if (finalScore <= 60) finalColor = 'red';
        else if (finalScore < 80) finalColor = 'yellow';
        else if (finalScore <= 90) finalColor = 'lightgreen';
        else finalColor = 'green';
      }

      // Map assignment type to valid data_type: observation, assignment, assessment
      let dataType = 'assignment';
      const rawType = (assignment.type || '').toLowerCase();
      if (rawType.includes('observ')) dataType = 'observation';
      else if (rawType.includes('test') || rawType.includes('quiz') || rawType.includes('assess')) dataType = 'assessment';

      payloads.push({ key: JSON.stringify([stuNum, 'academic', std.mlsCode]), payload: {
        standard_code: std.mlsCode,
        student_id: stuNum,
        title: assignment.title || 'Assignment Evaluation',
        data_type: dataType,
        score: finalScore,
        notes: cell.notes || null,
        evidence: null,
        color: finalColor,
        date: assignment.assessmentDate || assignment.dueDate || new Date().toISOString().split('T')[0],
      } });
    });
    components.forEach((component) => {
      if (!timelinessIsReady(assignment, grades, stu.studentId, component.key)) return;
      const status = submissionStatusFor(assignment, stu.studentId, component.key);
      payloads.push({ key: JSON.stringify([stuNum, 'submission', timingStandard, component.key]), payload: {
        standard_code: timingStandard,
        student_id: stuNum,
        title: `${assignment.title || 'Learning Task'} - Submission${component.key === 'task' ? '' : ` (${component.label})`}`,
        data_type: 'assignment',
        score: status.defaultScore,
        color: status.defaultColor,
        notes: `Submission: ${status.label}${component.key === 'task' ? '' : ` | Component: ${component.label}`}`,
        date: assignment.assessmentDate || assignment.dueDate || new Date().toISOString().split('T')[0],
      } });
    });
  });

  if (payloads.length === 0) {
    return {
      success: true,
      syncedCount: 0,
      total: 0,
      message: 'Demo students or no new graded scores to sync.',
    };
  }

  let successful = 0;
  const failures = [];
  // Save each server ID immediately so retrying a partial sync updates existing evidence.
  const syncedDataPoints = { ...(assignment.syncedDataPoints || {}) };
  for (const { key, payload } of payloads) {
    const previous = syncedDataPoints[key];
    try {
      const response = await authFetch(`${API_URL}/api/data-points/${previous || ''}`, {
        method: previous ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const detail = await response.json();
        const validationErrors = Array.isArray(detail.detail)
          ? detail.detail.map((item) => {
            const location = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
            return [location, item.msg].filter(Boolean).join(': ');
          }).filter(Boolean).join('; ')
          : '';
        const message = typeof detail.detail === 'string' ? detail.detail : validationErrors;
        throw new Error(message || `Server rejected data point (${response.status}).`);
      }
      const saved = await response.json();
      if (!Number.isInteger(saved.id)) throw new Error('Server did not return a saved data point ID.');
      syncedDataPoints[key] = saved.id;
      assignment.syncedDataPoints = { ...syncedDataPoints };
      await updateAssignment(assignment.id, { syncedDataPoints: { ...syncedDataPoints } }, teacherId);
      successful += 1;
    } catch (err: any) {
      console.warn('[AssignmentsService] Could not sync data point:', err);
      failures.push(`${payload.student_id} / ${payload.standard_code}: ${err.message}`);
    }
  }
  return {
    success: failures.length === 0,
    syncedCount: successful,
    total: payloads.length,
    count: successful,
    error: failures.length ? `${successful} of ${payloads.length} data points synced. ${failures.join(' ')}` : undefined,
  };
}
